import type { Subject } from 'rxjs';

import { REST } from '../api/ApiService';
import type { ITask } from '../domain';
import { OrchestratedItemTransformer } from '../orchestratedItem/orchestratedItem.transformer';
import { createDeferred } from '../utils/deferred';
import { diagnosticLogger } from '../utils/diagnosticLogger';

export class TaskReader {
  private static activeStatuses: string[] = ['RUNNING', 'SUSPENDED', 'NOT_STARTED'];

  public static getTasks(
    applicationName: string,
    statuses: string[] = [],
    limitPerPage: number = null,
    page: number = null,
  ): Promise<ITask[]> {
    return REST('/applications')
      .path(applicationName, 'tasks')
      .query({
        statuses: statuses.join(','),
        limit: limitPerPage,
        page: page,
      })
      .get()
      .then((tasks: ITask[]) => {
        tasks.forEach((task) => this.setTaskProperties(task));
        return tasks.filter((task) => !task.getValueFor('dryRun'));
      });
  }

  public static getRunningTasks(applicationName: string): Promise<ITask[]> {
    return this.getTasks(applicationName, this.activeStatuses);
  }

  public static getTask(taskId: string): Promise<ITask> {
    return REST('/tasks')
      .path(taskId)
      .get()
      .then((task: ITask) => {
        this.setTaskProperties(task);
        if (task.execution) {
          OrchestratedItemTransformer.defineProperties(task.execution);
          if (task.execution.stages) {
            task.execution.stages.forEach((stage: any) => OrchestratedItemTransformer.defineProperties(stage));
          }
        }
        return task;
      })
      .catch((error: any): undefined => {
        diagnosticLogger.warn('There was an issue retrieving taskId: ', taskId, error);
        return undefined;
      });
  }

  public static cancelPolling(task?: ITask): void {
    if (!task || task.poller === undefined) {
      return;
    }

    const poller = task.poller;
    clearTimeout(poller);
    if (task.poller === poller) {
      task.poller = undefined;
    }
  }

  public static waitUntilTaskMatches(
    task: ITask,
    closure: (task: ITask) => boolean,
    failureClosure?: (task: ITask) => boolean,
    interval = 1000,
    notifier?: Subject<void>,
  ): Promise<ITask> {
    const deferred = createDeferred<ITask>();
    if (!task) {
      deferred.reject(null);
    } else if (closure(task)) {
      this.cancelPolling(task);
      deferred.resolve(task);
    } else if (failureClosure && failureClosure(task)) {
      this.cancelPolling(task);
      deferred.reject(task);
    } else {
      this.cancelPolling(task);
      const poller = setTimeout(() => {
        if (task.poller !== poller) {
          return;
        }
        void Promise.resolve()
          .then(() => (task.poller === poller ? this.getTask(task.id) : undefined))
          .then((updated) => {
            if (task.poller !== poller) {
              return;
            }
            this.updateTask(task, updated);
            notifier?.next();
            if (task.poller !== poller) {
              return;
            }
            this.waitUntilTaskMatches(task, closure, failureClosure, interval, notifier).then(
              deferred.resolve,
              deferred.reject,
            );
          })
          .catch((error) => {
            if (task.poller === poller) {
              this.cancelPolling(task);
              deferred.reject(error);
            }
          });
      }, interval);
      task.poller = poller;
    }
    return deferred.promise;
  }

  public static waitUntilTaskCompletes(task: ITask, interval = 1000, notifier?: Subject<void>): Promise<ITask> {
    return this.waitUntilTaskMatches(
      task,
      (t) => t.isCompleted,
      (t) => t.isFailed,
      interval,
      notifier,
    );
  }

  /**
   * When polling for a match, (most of) the new task's properties are copied into the original task; if you need
   * some other property, you'll need to update this method
   */
  private static updateTask(original: ITask, updated?: ITask): void {
    if (!updated) {
      return;
    }
    original.status = updated.status;
    original.variables = updated.variables;
    original.steps = updated.steps;
    original.endTime = updated.endTime;
    original.execution = updated.execution;
    original.history = updated.history;
  }

  private static setTaskProperties(task: ITask): void {
    OrchestratedItemTransformer.defineProperties(task);
    if (task.steps && task.steps.length) {
      task.steps.forEach((step) => OrchestratedItemTransformer.defineProperties(step));
    }
  }
}
